import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteDataContext = createContext();

export const useSiteData = () => {
  const context = useContext(SiteDataContext);
  if (!context) {
    throw new Error('useSiteData must be used within a SiteDataProvider');
  }
  return context;
};

export const SiteDataProvider = ({ children }) => {
  const [companyInfo, setCompanyInfo] = useState({ name: '', phone: '', address: '', city: '', email: '', logo: '' });
  const [logoSize, setLogoSize] = useState(3);
  const [reprogrammingServices, setReprogrammingServices] = useState([]);
  const [tintingPrices, setTintingPrices] = useState([]);
  const [tintingBenefits, setTintingBenefits] = useState([]);
  const [services, setServices] = useState([]);
  const [heroImage, setHeroImage] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [pageContent, setPageContent] = useState({});
  const [cgv, setCgv] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-data`);
      const data = await res.json();
      setCompanyInfo(data.companyInfo);
      setLogoSize(data.logoSize ?? 3);
      setReprogrammingServices(data.reprogrammingServices);
      setTintingPrices(data.tintingPrices);
      setTintingBenefits(data.tintingBenefits);
      setServices(data.services);
      setHeroImage(data.heroImage);
      setGalleryImages(data.galleryImages);
      setPageContent(data.pageContent ?? {});
      setCgv(data.cgv ?? '');
    } catch (err) {
      console.error('Failed to fetch site data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveAll = async (overrides = {}) => {
    const payload = {
      companyInfo: overrides.companyInfo ?? companyInfo,
      logoSize: overrides.logoSize ?? logoSize,
      reprogrammingServices: overrides.reprogrammingServices ?? reprogrammingServices,
      tintingPrices: overrides.tintingPrices ?? tintingPrices,
      tintingBenefits: overrides.tintingBenefits ?? tintingBenefits,
      services: overrides.services ?? services,
      heroImage: overrides.heroImage ?? heroImage,
      galleryImages: overrides.galleryImages ?? galleryImages,
      pageContent: overrides.pageContent ?? pageContent,
      cgv: overrides.cgv ?? cgv,
    };
    const res = await fetch(`${API_URL}/api/site-data`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/upload`, {
      method: 'POST',
      body: formData,
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data.url;
  };

  const resetToDefaults = async () => {
    const res = await fetch(`${API_URL}/api/site-data`);
    const data = await res.json();
    // Will reload defaults from server
    await fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/60">Chargement...</div>
      </div>
    );
  }

  return (
    <SiteDataContext.Provider value={{
      companyInfo, setCompanyInfo,
      logoSize, setLogoSize,
      reprogrammingServices, setReprogrammingServices,
      tintingPrices, setTintingPrices,
      tintingBenefits, setTintingBenefits,
      services, setServices,
      heroImage, setHeroImage,
      galleryImages, setGalleryImages,
      pageContent, setPageContent,
      cgv, setCgv,
      saveAll, uploadImage, resetToDefaults, fetchData
    }}>
      {children}
    </SiteDataContext.Provider>
  );
};
